import { Check, ChevronDown, Copy, Eye, Filter } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Badge, Dropdown, Form } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { CustomDropDown } from "../../../../components/CustomDropDown";
import { CustomToggle } from "../../../../components/CustomToggle";
import ErrorMessageComponent from "../../../../components/ErrorMessageComponent";
import LoadingMessage from "../../../../components/LoadingMessage";
import { cycleColorMap, tierConfig } from "../../../../constants/constans";
import { useFirebaseProductOverview } from "../../../../hooks/useFirebaseProductOverview ";
import { useFirebaseProductSummaries } from "../../../../hooks/useFirebaseProductSummaries";
import {
  Allocations,
  Cart,
  CartSelection,
  Cycle,
  CycleSelection,
  ExtendedCartAllocationProps,
  Product,
} from "../../../../redux/allocationService";
import {
  useCreateCartAllocationMutation,
  useLazyGetCartAllocationBasicPlanQuery,
} from "../../../../redux/services/allocationService";
import {
  createProductUniqueKey,
  formatDateForAPISimple,
  formatTime,
} from "../../../../utility/inventoryUtils";
import "./index.css";

// Helper function to safely get classFilterTag
const getSafeClassFilterTag = (item: any): string => {
  if (!item || !item.classFilterTag) return "no tier";
  return item.classFilterTag.toLowerCase();
};

export default function CartAllocation({
  productionQuantities,
  selectedDate: displayDate,
  templateMode = false,
  singleDateMode = false,
  templateSourceDate,
  templateSaveToDate,
  isShowingLoadFromDate = false,
  isPastDate = false,
}: ExtendedCartAllocationProps) {
  const [fetchCartAllocation, { data: apiData, isLoading, error }] =
    useLazyGetCartAllocationBasicPlanQuery();
  const [createCartAllocation, { isLoading: isSaving, error: saveError }] =
    useCreateCartAllocationMutation();

  const [allocations, setAllocations] = useState<Allocations>({});
  const [originalAllocations, setOriginalAllocations] = useState<Allocations>({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [saveMessage, setSaveMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const getTodayDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [cycleSelections, setCycleSelections] = useState<CycleSelection[]>([]);
  const [showCycleDropdown, setShowCycleDropdown] = useState(false);

  const [cartSelections, setCartSelections] = useState<CartSelection[]>([]);
  const [showCartDropdown, setShowCartDropdown] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cartSearchQuery, setCartSearchQuery] = useState("");

  const [cartNameSearch, setCartNameSearch] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);

  const [saveToDate, setSaveToDate] = useState<string>(
    templateMode && templateSaveToDate
      ? templateSaveToDate
      : singleDateMode
        ? displayDate
        : getTodayDate(),
  );

  const templateToastShownRef = useRef(false);
  const singleDateToastShownRef = useRef(false);

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id,
  );

  const allCarts = useMemo(() => {
    if (!apiData?.cartsHeader) return [];
    return (apiData.cartsHeader || [])
      .filter(
        (cart) =>
          cart.cartType === "fixed" ||
          cart.cartType === "Fixed" ||
          cart.cartType === "moving" ||
          cart.cartType === "Moving",
      )
      .map((cart) => ({
        id: cart.id,
        displayName: cart.displayName,
        priority: cart.priority,
        abbreviation: cart.abbreviation,
        isFixedCart: cart.isFixedCart,
      }));
  }, [apiData]);

  useEffect(() => {
    if (templateMode && templateSaveToDate) {
      setSaveToDate(templateSaveToDate);
    } else if (singleDateMode) {
      setSaveToDate(displayDate);
    }
  }, [templateMode, singleDateMode, templateSaveToDate, displayDate]);

  const hasExistingAllocations = useMemo(() => {
    return (
      apiData?.cartAllocations?.id &&
      apiData?.cartAllocations?.carts &&
      Array.isArray(apiData.cartAllocations.carts) &&
      apiData.cartAllocations.carts.length > 0
    );
  }, [apiData]);

  useEffect(() => {
    if (restaurantId && displayDate) {
      const formattedDate = formatDateForAPISimple(displayDate);
      fetchCartAllocation({ restaurantId, date: formattedDate });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, displayDate]);

  useEffect(() => {
    if (apiData?.productionCycle) {
      const transformedCycles: Cycle[] = (apiData.productionCycle || []).map(
        (cycle, index) => ({
          id: cycle?.id || `cycle-${index}`,
          displayName: cycle?.displayName || `Cycle ${index + 1}`,
          color:
            Object.keys(cycleColorMap)[
              index % Object.keys(cycleColorMap).length
            ],
          time:
            cycle?.startTime && cycle?.endTime
              ? `${formatTime(cycle.startTime, true)} - ${formatTime(cycle.endTime, true)}`
              : "00:00 - 23:59",
          startTime: cycle?.startTime,
          endTime: cycle?.endTime,
          productionPlanId: cycle?.planId,
          items: (cycle?.items || []).map((item) => ({
            itemId: item?.itemId || "",
            displayName: item?.displayName || "",
            portionSize: item?.portionSize || "",
            quantity: item?.quantity || 0,
            classFilterTag: getSafeClassFilterTag(item),
          })),
        }),
      );

      const selections = transformedCycles.map((cycle) => ({
        id: cycle.id,
        displayName: cycle.displayName,
        selected: true,
      }));

      setCycleSelections(selections);
    }
  }, [apiData]);

  useEffect(() => {
    if (apiData?.cartsHeader) {
      const filteredCarts = (apiData.cartsHeader || []).filter(
        (cart) =>
          cart.cartType === "fixed" ||
          cart.cartType === "Fixed" ||
          cart.cartType === "moving" ||
          cart.cartType === "Moving",
      );

      const selections = filteredCarts.map((cart) => ({
        id: cart.id,
        displayName: cart.displayName || "",
        abbreviation: cart.abbreviation || "",
        selected: true,
      }));

      setCartSelections(selections);
    }
  }, [apiData]);

  const selectedCycles = useMemo(() => {
    return cycleSelections.filter((cycle) => cycle.selected);
  }, [cycleSelections]);

  const selectedCarts = useMemo(() => {
    return cartSelections.filter((cart) => cart.selected);
  }, [cartSelections]);

  const filteredCartSelections = useMemo(() => {
    if (!cartSearchQuery.trim()) return cartSelections;
    const query = cartSearchQuery.toLowerCase().trim();
    return cartSelections.filter(
      (cart) =>
        cart.displayName.toLowerCase().includes(query) ||
        (cart.abbreviation && cart.abbreviation.toLowerCase().includes(query)),
    );
  }, [cartSelections, cartSearchQuery]);

  const allCycles = useMemo(() => {
    if (!apiData?.productionCycle) return [];
    return (apiData.productionCycle || []).map((cycle, index) => ({
      id: cycle?.id || `cycle-${index}`,
      displayName: cycle?.displayName || `Cycle ${index + 1}`,
      color:
        Object.keys(cycleColorMap)[index % Object.keys(cycleColorMap).length],
      time:
        cycle?.startTime && cycle?.endTime
          ? `${formatTime(cycle.startTime, false)} - ${formatTime(cycle.endTime, false)}`
          : "00:00 - 23:59",
      startTime: cycle?.startTime,
      endTime: cycle?.endTime,
      productionPlanId: cycle?.planId,
      items: (cycle?.items || []).map((item) => ({
        itemId: item?.itemId || "",
        displayName: item?.displayName || "",
        portionSize: item?.portionSize || "",
        quantity: item?.quantity || 0,
        classFilterTag: getSafeClassFilterTag(item),
        productId: item?.productId || "",
      })),
    }));
  }, [apiData]);

  const currentViewDate = useMemo(() => {
    if (!displayDate) return new Date();
    const [year, month, day] = displayDate.split("-").map(Number);
    return new Date(year, month - 1, day);
  }, [displayDate]);

  const { data: productOverviewData, loading: overviewLoading } =
    useFirebaseProductOverview(restaurantId, currentViewDate);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: productSummaries, loading: summariesLoading } =
    useFirebaseProductSummaries(restaurantId, currentViewDate);

  const reservedQuantitiesMap = useMemo(() => {
    const map = new Map<string, number>();
    if (productOverviewData && Array.isArray(productOverviewData)) {
      productOverviewData.forEach((product) => {
        if (product.itemId && product.quantities?.reserved) {
          map.set(product.itemId, product.quantities.reserved);
        }
      });
    }
    return map;
  }, [productOverviewData]);

  const cancelledQuantitiesMap = useMemo(() => {
    const map = new Map<string, number>();
    if (productOverviewData && Array.isArray(productOverviewData)) {
      productOverviewData.forEach((product) => {
        if (product.itemId && product.quantities?.cancelled) {
          map.set(product.itemId, product.quantities.cancelled);
        }
      });
    }
    return map;
  }, [productOverviewData]);

  // ─── Build a productId lookup map keyed by itemId from all cycle items ───
  const productIdByItemId = useMemo(() => {
    const map = new Map<string, string>();
    if (apiData?.productionCycle) {
      for (const cycle of apiData.productionCycle) {
        for (const item of cycle.items || []) {
          if (item?.itemId && item?.productId) {
            map.set(item.itemId, item.productId);
          }
        }
      }
    }
    return map;
  }, [apiData]);

  // ─── Build full item metadata lookup keyed by itemId ───
  // Sources (in priority order for each field):
  //   1. productionCycle items  — best source for portionSize, classFilterTag, productId
  //   2. cartAllocations products — best source for skuId (cycle items rarely carry it)
  //
  // For portionSize/classFilterTag we prefer the richer cycle entry (second batch
  // which has portionSize:"REGULAR" and a real classFilterTag) over the sparse
  // first batch. For skuId we fall back to cartAllocations when the cycle item
  // has none, because that is where skuIds are reliably stored.
  const itemMetaByItemId = useMemo(() => {
    const map = new Map<
      string,
      {
        productId: string;
        portionSize: string;
        classFilterTag: string;
        skuId: string;
        displayName: string;
      }
    >();

    // ── Pass 1: seed from productionCycle items ──────────────────────────────
    if (apiData?.productionCycle) {
      for (const cycle of apiData.productionCycle) {
        for (const rawItem of cycle.items || []) {
          const item = rawItem as any;
          if (!item?.itemId) continue;

          const existing = map.get(item.itemId);
          const incomingHasRichData =
            (item.portionSize && item.portionSize !== "") ||
            (item.classFilterTag &&
              item.classFilterTag !== "" &&
              item.classFilterTag.toLowerCase() !== "no tier");

          // Always write if no entry yet; overwrite only when incoming is richer.
          if (!existing || incomingHasRichData) {
            map.set(item.itemId, {
              productId: item.productId || existing?.productId || "",
              portionSize: item.portionSize || existing?.portionSize || "",
              classFilterTag: getSafeClassFilterTag(item) || existing?.classFilterTag || "",
              // Preserve any skuId already captured; cycle items rarely have it.
              skuId: item.skuId || existing?.skuId || "",
              displayName: item.displayName || existing?.displayName || "",
            });
          }
        }
      }
    }

    // ── Pass 2: backfill skuId (and any other missing fields) from
    //    cartAllocations — this is the reliable source for skuId ─────────────
    if (apiData?.cartAllocations?.carts) {
      for (const cart of apiData.cartAllocations.carts as any[]) {
        for (const cycle of cart.cycles || []) {
          for (const product of cycle.products || []) {
            if (!product?.itemId) continue;
            const existing = map.get(product.itemId);
            if (existing) {
              // Only patch fields that are still empty.
              map.set(product.itemId, {
                productId: existing.productId || product.productId || "",
                portionSize: existing.portionSize || product.portionSize || "",
                classFilterTag: existing.classFilterTag || getSafeClassFilterTag(product),
                skuId: existing.skuId || product.skuId || "",
                displayName: existing.displayName || product.displayName || "",
              });
            } else {
              // Item only exists in cartAllocations, not in cycle — add it.
              map.set(product.itemId, {
                productId: product.productId || "",
                portionSize: product.portionSize || "",
                classFilterTag: getSafeClassFilterTag(product),
                skuId: product.skuId || "",
                displayName: product.displayName || "",
              });
            }
          }
        }
      }
    }

    return map;
  }, [apiData]);

  // ─── getProductId: resolves productId from itemId ───
  const getProductId = useCallback(
    (itemId: string): string => {
      return productIdByItemId.get(itemId) || "";
    },
    [productIdByItemId],
  );

  const toggleCycleSelection = (cycleId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCycleSelections((prev) =>
      prev.map((cycle) =>
        cycle.id === cycleId ? { ...cycle, selected: !cycle.selected } : cycle,
      ),
    );
  };

  const toggleCartSelection = (cartId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCartSelections((prev) =>
      prev.map((cart) =>
        cart.id === cartId ? { ...cart, selected: !cart.selected } : cart,
      ),
    );
  };

  const selectAllCycles = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCycleSelections((prev) => prev.map((cycle) => ({ ...cycle, selected: true })));
  };

  const deselectAllCycles = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCycleSelections((prev) => prev.map((cycle) => ({ ...cycle, selected: false })));
  };

  const selectAllCarts = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCartSelections((prev) => prev.map((cart) => ({ ...cart, selected: true })));
  };

  const deselectAllCarts = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCartSelections((prev) => prev.map((cart) => ({ ...cart, selected: false })));
  };

  const handleCartNameSearch = (query: string) => {
    setCartNameSearch(query);
    setIsSearchActive(query.trim() !== "");
  };

  useEffect(() => {
    if (templateMode && templateSourceDate && displayDate && saveToDate) {
      const toastId = `cart-template-${displayDate}-${saveToDate}`;
      if (!toast.isActive(toastId) && !templateToastShownRef.current) {
        if (isShowingLoadFromDate) {
          toast.info(
            `Template: Showing cart allocations from ${templateSourceDate} for ${displayDate}. You can edit and save to ${saveToDate}.`,
            { autoClose: 3000, toastId },
          );
        }
        templateToastShownRef.current = true;
      }
    } else if (singleDateMode && displayDate !== getTodayDate()) {
      const toastId = `cart-single-${displayDate}`;
      if (!toast.isActive(toastId) && !singleDateToastShownRef.current) {
        toast.info(
          `Showing cart allocations for ${displayDate}. You can edit and save to this date.`,
          { autoClose: 3000, toastId },
        );
        singleDateToastShownRef.current = true;
      }
    }
    return () => {
      templateToastShownRef.current = false;
      singleDateToastShownRef.current = false;
    };
  }, [templateMode, singleDateMode, templateSourceDate, displayDate, saveToDate, isShowingLoadFromDate]);

  useEffect(() => {
    if (saveError) {
      setSaveMessage({ type: "error", text: "Failed to save cart allocation. Please try again." });
      setTimeout(() => setSaveMessage(null), 5000);
    }
  }, [saveError]);

  useEffect(() => {
    if (hasExistingAllocations) {
      setIsUpdateMode(true);
    } else {
      setIsUpdateMode(false);
    }
  }, [hasExistingAllocations, apiData]);

  useEffect(() => {
    if (apiData?.cartAllocations?.carts) {
      const existingAllocations: Allocations = {};

      apiData.cartAllocations.carts.forEach((cart: any) => {
        cart.cycles?.forEach((cycle: any) => {
          cycle.products?.forEach((product: any) => {
            const uniqueKey = createProductUniqueKey(product.itemId);

            if (!existingAllocations[uniqueKey]) {
              existingAllocations[uniqueKey] = {};
            }
            if (!existingAllocations[uniqueKey][cart.cartId]) {
              existingAllocations[uniqueKey][cart.cartId] = {};
            }

            existingAllocations[uniqueKey][cart.cartId][cycle.cycleId] =
              product.totalAllocated || 0;
          });
        });
      });

      setAllocations(existingAllocations);
      setOriginalAllocations(JSON.parse(JSON.stringify(existingAllocations)));
    } else if (apiData?.productionCycle) {
      const emptyAllocations: Allocations = {};
      const transformedCycles: Cycle[] = allCycles;

      const filteredCarts: Cart[] = (apiData.cartsHeader || [])
        .filter(
          (cart) =>
            (cart.cartType === "fixed" ||
              cart.cartType === "Fixed" ||
              cart.cartType === "moving" ||
              cart.cartType === "Moving") &&
            cartSelections.some(
              (selectedCart) => selectedCart.id === cart.id && selectedCart.selected,
            ),
        )
        .map((cart) => ({
          id: cart.id,
          displayName: cart.displayName,
          priority: cart.priority,
          abbreviation: cart.abbreviation,
          isFixedCart: cart.isFixedCart,
        }));

      const allProductsMap = new Map<string, Product>();

      transformedCycles.forEach((cycle) => {
        cycle.items?.forEach((item) => {
          const uniqueKey = createProductUniqueKey(item.itemId);

          if (!allProductsMap.has(uniqueKey)) {
            const cycleProduction: Record<string, number> = {};
            transformedCycles.forEach((c) => { cycleProduction[c.id] = 0; });

            const meta = itemMetaByItemId.get(item.itemId);

            allProductsMap.set(uniqueKey, {
              itemId: item.itemId,
              productId: meta?.productId || (item as any).productId || "",
              displayName: item.displayName,
              portionSize: meta?.portionSize || item.portionSize || "",
              classFilterTag: (meta?.classFilterTag || item.classFilterTag) as
                "gold" | "bronze" | "platinum" | "silver",
              cycleProduction,
              uniqueKey,
              skuId: meta?.skuId || "",
            });
          }

          const product = allProductsMap.get(uniqueKey)!;
          product.cycleProduction[cycle.id] = item.quantity;
        });
      });

      Array.from(allProductsMap.values()).forEach((product) => {
        if (!emptyAllocations[product.uniqueKey]) {
          emptyAllocations[product.uniqueKey] = {};
        }
        filteredCarts.forEach((cart) => {
          if (!emptyAllocations[product.uniqueKey][cart.id]) {
            emptyAllocations[product.uniqueKey][cart.id] = {};
          }
          transformedCycles.forEach((cycle) => {
            emptyAllocations[product.uniqueKey][cart.id][cycle.id] = 0;
          });
        });
      });

      setAllocations(emptyAllocations);
      setOriginalAllocations(JSON.parse(JSON.stringify(emptyAllocations)));
    }
  }, [apiData, allCycles, cartSelections, itemMetaByItemId]);

  const { cycles, carts, products } = useMemo(() => {
    if (!apiData || !apiData.productionCycle) {
      return { cycles: [], carts: [], products: [], cartHeaders: [] };
    }

    const filteredCycles = allCycles.filter((cycle) =>
      selectedCycles.some((selected) => selected.id === cycle.id),
    );

    const transformedCycles: Cycle[] = filteredCycles.map((cycle) => ({
      id: cycle.id,
      displayName: cycle.displayName,
      color: cycle.color,
      time: cycle.time,
      startTime: cycle.startTime,
      endTime: cycle.endTime,
      productionPlanId: cycle.productionPlanId,
      items: cycle.items,
    }));

    const transformedCarts: Cart[] = (apiData.cartsHeader || [])
      .filter(
        (cart) =>
          (cart.cartType === "fixed" ||
            cart.cartType === "Fixed" ||
            cart.cartType === "moving" ||
            cart.cartType === "Moving") &&
          selectedCarts.some((selectedCart) => selectedCart.id === cart.id) &&
          (!isSearchActive ||
            cartNameSearch.trim() === "" ||
            cart.displayName.toLowerCase().includes(cartNameSearch.toLowerCase().trim()) ||
            (cart.abbreviation &&
              cart.abbreviation.toLowerCase().includes(cartNameSearch.toLowerCase().trim()))),
      )
      .map((cart) => ({
        id: cart.id,
        displayName: cart.displayName,
        priority: cart.priority,
        abbreviation: cart.abbreviation,
        isFixedCart: cart.isFixedCart,
      }));

    const filteredCartHeaders = (apiData.cartsHeader || []).filter(
      (cart) =>
        (cart.cartType === "fixed" ||
          cart.cartType === "Fixed" ||
          cart.cartType === "moving" ||
          cart.cartType === "Moving") &&
        selectedCarts.some((selectedCart) => selectedCart.id === cart.id) &&
        (!isSearchActive ||
          cartNameSearch.trim() === "" ||
          cart.displayName.toLowerCase().includes(cartNameSearch.toLowerCase().trim()) ||
          (cart.abbreviation &&
            cart.abbreviation.toLowerCase().includes(cartNameSearch.toLowerCase().trim()))),
    );

    const allProductsMap = new Map<string, Product>();

    transformedCycles.forEach((cycle) => {
      cycle.items?.forEach((item) => {
        const uniqueKey = createProductUniqueKey(item.itemId);

        if (!allProductsMap.has(uniqueKey)) {
          const cycleProduction: Record<string, number> = {};
          transformedCycles.forEach((c) => { cycleProduction[c.id] = 0; });

          // ─── Pull all metadata from itemMetaByItemId ───
          const meta = itemMetaByItemId.get(item.itemId);

          allProductsMap.set(uniqueKey, {
            itemId: item.itemId,
            productId: meta?.productId || (item as any).productId || "",
            displayName: item.displayName,
            portionSize: meta?.portionSize || item.portionSize || "",
            classFilterTag: (meta?.classFilterTag || item.classFilterTag) as
              "gold" | "bronze" | "platinum" | "silver",
            cycleProduction,
            uniqueKey,
            skuId: meta?.skuId || "",
          });
        }

        const product = allProductsMap.get(uniqueKey)!;
        product.cycleProduction[cycle.id] = item.quantity;
      });
    });

    const transformedProducts = Array.from(allProductsMap.values());
    transformedProducts.sort((a, b) => a.itemId.localeCompare(b.itemId));

    return {
      cycles: transformedCycles,
      carts: transformedCarts,
      products: transformedProducts,
      cartHeaders: filteredCartHeaders,
    };
  }, [apiData, allCycles, selectedCycles, selectedCarts, cartNameSearch, isSearchActive, itemMetaByItemId]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--cycles-count",
      cycles.length.toString(),
    );
  }, [cycles.length]);

  const getTimestampValue = (timestamp: any): number | null => {
    if (!timestamp) return null;
    if (timestamp._seconds !== undefined) return timestamp._seconds * 1000;
    if (timestamp.seconds !== undefined) return timestamp.seconds * 1000;
    if (typeof timestamp === "string") return new Date(timestamp).getTime();
    return null;
  };

  const getEarliestCycleForProduct = useCallback(
    (product: Product): Cycle | null => {
      if (cycles.length === 0) return null;
      let earliestCycle: Cycle | null = null;
      let earliestTime: number | null = null;
      cycles.forEach((cycle) => {
        if (product.cycleProduction[cycle.id] > 0) {
          const cycleTime = getTimestampValue(cycle.startTime);
          if (cycleTime !== null) {
            if (earliestTime === null || cycleTime < earliestTime) {
              earliestTime = cycleTime;
              earliestCycle = cycle;
            }
          }
        }
      });
      return earliestCycle;
    },
    [cycles],
  );

  const calculateAvailableQuantity = useCallback(
    (product: Product): number => {
      const earliestCycle = getEarliestCycleForProduct(product);
      if (!earliestCycle) return 0;

      const reservedQty = reservedQuantitiesMap.get(product.itemId) || 0;
      const cancelledQty = cancelledQuantitiesMap.get(product.itemId) || 0;
      const productionFromEarliestCycle = product.cycleProduction[earliestCycle.id] || 0;

      let totalAllocatedInEarliestCycle = 0;
      allCarts.forEach((cart) => {
        totalAllocatedInEarliestCycle +=
          allocations[product.uniqueKey]?.[cart.id]?.[earliestCycle.id] || 0;
      });

      return productionFromEarliestCycle - totalAllocatedInEarliestCycle - reservedQty + cancelledQty;
    },
    [getEarliestCycleForProduct, reservedQuantitiesMap, cancelledQuantitiesMap, allocations, allCarts],
  );

  const calculateAllocatedFromEarliestCycle = useCallback(
    (product: Product): number => {
      const earliestCycle = getEarliestCycleForProduct(product);
      if (!earliestCycle) return 0;
      let totalAllocated = 0;
      allCarts.forEach((cart) => {
        totalAllocated += allocations[product.uniqueKey]?.[cart.id]?.[earliestCycle.id] || 0;
      });
      return totalAllocated;
    },
    [getEarliestCycleForProduct, allocations, allCarts],
  );

  const getAllocationError = useCallback(
    (uniqueKey: string, cycleId: string): string | null => {
      const product = products.find((p) => p.uniqueKey === uniqueKey);
      if (!product) return null;
      const cycleTotalProduction = product.cycleProduction[cycleId] || 0;
      let totalAllocated = 0;
      allCarts.forEach((cart) => {
        totalAllocated += allocations[uniqueKey]?.[cart.id]?.[cycleId] || 0;
      });
      if (totalAllocated > cycleTotalProduction) {
        return `Exceeds production by ${totalAllocated - cycleTotalProduction}`;
      }
      return null;
    },
    [products, allCarts, allocations],
  );

  const getAvailableAllocation = useCallback(
    (uniqueKey: string, cartId: string, cycleId: string): number => {
      const product = products.find((p) => p.uniqueKey === uniqueKey);
      if (!product) return 0;
      const cycleTotalProduction = product.cycleProduction[cycleId] || 0;
      const otherCartsAllocation = allCarts.reduce((sum, cart) => {
        if (cart.id === cartId) return sum;
        return sum + (allocations[uniqueKey]?.[cart.id]?.[cycleId] || 0);
      }, 0);
      return Math.max(0, cycleTotalProduction - otherCartsAllocation);
    },
    [products, allCarts, allocations],
  );

  const updateAllocation = useCallback(
    (uniqueKey: string, cartId: string, cycleId: string, value: number) => {
      const product = products.find((p) => p.uniqueKey === uniqueKey);
      if (!product) return;

      const maxAllocation = product.cycleProduction[cycleId] || 0;

      if (value < 0) {
        toast.error("Allocation cannot be negative");
        return;
      }

      const otherCartsAllocation = allCarts.reduce((sum, cart) => {
        if (cart.id === cartId) return sum;
        return sum + (allocations[uniqueKey]?.[cart.id]?.[cycleId] || 0);
      }, 0);

      const maxAllowedForThisCart = Math.max(0, maxAllocation - otherCartsAllocation);

      if (value === 0) {
        setAllocations((prev) => ({
          ...prev,
          [uniqueKey]: {
            ...prev[uniqueKey],
            [cartId]: { ...(prev[uniqueKey]?.[cartId] || {}), [cycleId]: 0 },
          },
        }));
        return;
      }

      const finalValue = Math.min(value, maxAllowedForThisCart);
      if (finalValue < value) {
        toast.warning(
          `Capped at ${finalValue}. Maximum allowed for this cart is ${maxAllowedForThisCart} (${maxAllocation} total - ${otherCartsAllocation} allocated to other carts)`,
        );
      }

      setAllocations((prev) => ({
        ...prev,
        [uniqueKey]: {
          ...prev[uniqueKey],
          [cartId]: { ...(prev[uniqueKey]?.[cartId] || {}), [cycleId]: finalValue },
        },
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products, allCarts, allocations],
  );

  const getCartTotal = useCallback(
    (uniqueKey: string, cartId: string) => {
      const alloc = allocations[uniqueKey]?.[cartId];
      if (!alloc) return 0;
      return Object.values(alloc).reduce((sum, val) => sum + (val || 0), 0);
    },
    [allocations],
  );

  const getCartCycleTotal = useCallback(
    (cartId: string) => {
      return products.reduce(
        (sum, product) => sum + getCartTotal(product.uniqueKey, cartId),
        0,
      );
    },
    [products, getCartTotal],
  );

  const getCycleTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    cycles.forEach((cycle) => {
      totals[cycle.id] = products.reduce((sum, p) => sum + (p.cycleProduction[cycle.id] || 0), 0);
    });
    return totals;
  }, [cycles, products]);

  const hasAllocationChanged = (uniqueKey: string, cartId: string, cycleId: string): boolean => {
    const currentValue = allocations[uniqueKey]?.[cartId]?.[cycleId] || 0;
    const originalValue = originalAllocations[uniqueKey]?.[cartId]?.[cycleId] || 0;
    return currentValue !== originalValue;
  };

  const hasAnyAllocationChanged = (): boolean => {
    for (const product of products) {
      for (const cart of allCarts) {
        for (const cycle of cycles) {
          if (hasAllocationChanged(product.uniqueKey, cart.id, cycle.id)) return true;
        }
      }
    }
    return false;
  };

  const validateAllAllocations = (): boolean => {
    const errors: string[] = [];
    products.forEach((product) => {
      cycles.forEach((cycle) => {
        const cycleTotalProduction = product.cycleProduction[cycle.id] || 0;
        let totalAllocated = 0;
        allCarts.forEach((cart) => {
          totalAllocated += allocations[product.uniqueKey]?.[cart.id]?.[cycle.id] || 0;
        });
        if (totalAllocated > cycleTotalProduction) {
          errors.push(
            `${product.displayName} (${product.portionSize}): Cycle ${cycle.displayName} - ` +
              `Allocated ${totalAllocated} but production is only ${cycleTotalProduction}`,
          );
        }
      });
    });

    if (errors.length > 0) {
      toast.error(
        <div>
          <strong>Validation Errors:</strong>
          <ul style={{ margin: "5px 0", paddingLeft: "20px" }}>
            {errors.slice(0, 3).map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
          {errors.length > 3 && <div>...and {errors.length - 3} more errors</div>}
        </div>,
        { autoClose: 10000 },
      );
      return false;
    }
    return true;
  };

  const handleSaveAllocation = async () => {
    if (!restaurantId || !apiData) {
      setSaveMessage({ type: "error", text: "No restaurant or allocation data available" });
      setTimeout(() => setSaveMessage(null), 5000);
      return;
    }

    if (!validateAllAllocations()) return;

    try {
      const cycleReferences = cycles.map((cycle) => ({
        cycleId: cycle.id,
        cycleName: cycle.displayName,
        productionPlanId: cycle.productionPlanId || "",
      }));

      const cartAllocations: any[] = [];
      const productAllocationMap = new Map<string, { itemId: string; quantity: number }>();

      carts.forEach((cart) => {
        const cartAllocation: any = {
          cartId: cart.id,
          cartName: cart.displayName,
          cycles: [],
          isCartAllocated: false,
        };

        const cycleProductsMap: Record<string, any[]> = {};

        cycles.forEach((cycle) => {
          cycleProductsMap[cycle.id] = [];

          products.forEach((product) => {
            const allocatedQty = allocations[product.uniqueKey]?.[cart.id]?.[cycle.id] || 0;

            if (!productAllocationMap.has(product.itemId)) {
              productAllocationMap.set(product.itemId, {
                itemId: product.itemId,
                quantity: allocatedQty,
              });
            } else {
              const existing = productAllocationMap.get(product.itemId)!;
              productAllocationMap.set(product.itemId, {
                ...existing,
                quantity: existing.quantity + allocatedQty,
              });
            }

            // ─── FIX: always resolve productId, portionSize, classFilterTag
            //         from itemMetaByItemId so they are never empty ───
            const meta = itemMetaByItemId.get(product.itemId);
            const resolvedProductId =
              meta?.productId || product.productId || getProductId(product.itemId) || "";
            const resolvedPortionSize =
              meta?.portionSize || product.portionSize || "";
            const rawTag = meta?.classFilterTag || product.classFilterTag || "";
            const resolvedClassFilterTag = rawTag && rawTag.trim() !== ""
              ? rawTag.charAt(0).toUpperCase() + rawTag.slice(1)
              : "No tier";
            const resolvedSkuId = meta?.skuId || product.skuId || "";

            cycleProductsMap[cycle.id].push({
              itemId: product.itemId,
              productId: resolvedProductId,
              displayName: product.displayName,
              portionSize: resolvedPortionSize,
              skuId: resolvedSkuId,
              classFilterTag: resolvedClassFilterTag,
              totalAllocated: allocatedQty,
            });
          });
        });

        cycles.forEach((cycle) => {
          const productsInCycle = cycleProductsMap[cycle.id].filter((product) => {
            const productInCycle = products.find(
              (p) => p.itemId === product.itemId && p.cycleProduction[cycle.id] > 0,
            );
            return productInCycle !== undefined;
          });

          if (productsInCycle.length > 0) {
            cartAllocation.cycles.push({
              cycleId: cycle.id,
              cycleName: cycle.displayName,
              productionPlanId: cycle.productionPlanId || "",
              products: productsInCycle,
            });
          }
        });

        cartAllocations.push(cartAllocation);
      });

      const productAllocation = Array.from(productAllocationMap.values()).filter(
        (item) => item.quantity > 0,
      );
      const formattedSaveDate = formatDateForAPISimple(saveToDate);

      const payload = {
        restaurantId,
        date: formattedSaveDate,
        productAllocation,
        allocationPlan: {
          status: "ACTIVE",
          totalCarts: carts.length,
          cycleReference: cycleReferences,
        },
        cartAllocations,
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await createCartAllocation(payload).unwrap();

      if (templateMode) {
        if (isShowingLoadFromDate) {
          toast.success(`Allocation saved from ${displayDate} to template date: ${saveToDate}!`);
        } else {
          toast.success(`Allocation saved for template date: ${saveToDate}!`);
        }
      } else if (singleDateMode && displayDate !== getTodayDate()) {
        toast.success(`Allocation saved for ${displayDate}!`);
      } else {
        toast.success(`Allocation saved for ${saveToDate}!`);
      }

      setIsUpdateMode(true);
      setOriginalAllocations(JSON.parse(JSON.stringify(allocations)));

      const formattedLoadDate = formatDateForAPISimple(displayDate);
      fetchCartAllocation({ restaurantId, date: formattedLoadDate });
    } catch (error) {
      toast.error("Error occurred while saving allocation");
    }
  };

  const handleUpdateAllocation = async () => {
    if (!restaurantId || !apiData) {
      setSaveMessage({ type: "error", text: "No restaurant or allocation data available" });
      setTimeout(() => setSaveMessage(null), 5000);
      return;
    }

    if (!hasAnyAllocationChanged()) {
      toast.info("No changes detected to update");
      return;
    }

    if (!validateAllAllocations()) return;

    try {
      const allocationPlanId = apiData.cartAllocations?.id;
      if (!allocationPlanId) {
        toast.error("No existing allocation plan found to update");
        return;
      }

      const cartsWithChanges: string[] = [];
      const changedAllocations: Record<
        string,
        Record<string, Record<string, number>>
      > = {};

      carts.forEach((cart) => {
        let cartHasChanges = false;
        const cartChanges: Record<string, Record<string, number>> = {};

        products.forEach((product) => {
          const productChanges: Record<string, number> = {};
          cycles.forEach((cycle) => {
            if (hasAllocationChanged(product.uniqueKey, cart.id, cycle.id)) {
              productChanges[cycle.id] =
                allocations[product.uniqueKey]?.[cart.id]?.[cycle.id] || 0;
              cartHasChanges = true;
            }
          });
          if (Object.keys(productChanges).length > 0) {
            cartChanges[product.uniqueKey] = productChanges;
          }
        });

        if (cartHasChanges) {
          cartsWithChanges.push(cart.id);
          changedAllocations[cart.id] = cartChanges;
        }
      });

      if (cartsWithChanges.length === 0) {
        toast.info("No changes detected to update");
        return;
      }

      const cartAllocations: any[] = [];
      const productAllocationMap = new Map<string, { itemId: string; quantity: number }>();

      Object.keys(originalAllocations).forEach((productKey) => {
        const product = products.find((p) => p.uniqueKey === productKey);
        if (!product) return;
        let totalQuantity = 0;
        carts.forEach((cart) => {
          cycles.forEach((cycle) => {
            totalQuantity += allocations[productKey]?.[cart.id]?.[cycle.id] || 0;
          });
        });
        productAllocationMap.set(product.itemId, {
          itemId: product.itemId,
          quantity: totalQuantity,
        });
      });

      carts.forEach((cart) => {
        if (!cartsWithChanges.includes(cart.id)) return;

        const cartAllocation: any = {
          cartId: cart.id,
          cartName: cart.displayName,
          cycles: [],
          isCartAllocated: false,
        };

        const cartChanges = changedAllocations[cart.id];
        if (!cartChanges) return;

        const cycleProductsMap: Record<string, any[]> = {};

        cycles.forEach((cycle) => {
          cycleProductsMap[cycle.id] = [];

          Object.keys(cartChanges).forEach((productKey) => {
            const productChanges = cartChanges[productKey];
            if (productChanges[cycle.id] !== undefined) {
              const product = products.find((p) => p.uniqueKey === productKey);
              if (!product) return;

              const allocatedQty = productChanges[cycle.id];

              // ─── FIX: resolve all metadata from itemMetaByItemId ───
              const meta = itemMetaByItemId.get(product.itemId);
              const resolvedProductId =
                meta?.productId || product.productId || getProductId(product.itemId) || "";
              const resolvedPortionSize =
                meta?.portionSize || product.portionSize || "";
              const rawTag = meta?.classFilterTag || product.classFilterTag || "";
              const resolvedClassFilterTag = rawTag && rawTag.trim() !== ""
                ? rawTag.charAt(0).toUpperCase() + rawTag.slice(1)
                : "No tier";
              const resolvedSkuId = meta?.skuId || product.skuId || "";

              cycleProductsMap[cycle.id].push({
                itemId: product.itemId,
                productId: resolvedProductId,
                displayName: product.displayName,
                portionSize: resolvedPortionSize,
                skuId: resolvedSkuId,
                classFilterTag: resolvedClassFilterTag,
                totalAllocated: allocatedQty,
              });
            }
          });
        });

        cycles.forEach((cycle) => {
          const productsInCycle = cycleProductsMap[cycle.id];
          if (productsInCycle && productsInCycle.length > 0) {
            cartAllocation.cycles.push({
              cycleId: cycle.id,
              cycleName: cycle.displayName,
              productionPlanId: cycle.productionPlanId || "",
              products: productsInCycle,
            });
          }
        });

        if (cartAllocation.cycles.length > 0) {
          cartAllocations.push(cartAllocation);
        }
      });

      const cyclesWithChanges = cycles.filter((cycle) =>
        cartAllocations.some((cartAlloc) =>
          cartAlloc.cycles.some((c: any) => c.cycleId === cycle.id),
        ),
      );

      const cycleReferences = cyclesWithChanges.map((cycle) => ({
        cycleId: cycle.id,
        cycleName: cycle.displayName,
        productionPlanId: cycle.productionPlanId || "",
      }));

      const productAllocation = Array.from(productAllocationMap.values()).filter(
        (item) => item.quantity > 0,
      );
      const formattedSaveDate = formatDateForAPISimple(saveToDate);

      const payload = {
        restaurantId,
        allocationPlanId,
        date: formattedSaveDate,
        productAllocation,
        allocationPlan: { status: "ACTIVE", cycleReference: cycleReferences },
        cartAllocations,
      };

      const response = await createCartAllocation(payload).unwrap();

      if (response.success) {
        if (templateMode) {
          if (isShowingLoadFromDate) {
            toast.success(
              `Allocation updated from ${displayDate} to template date: ${saveToDate}!`,
            );
          } else {
            toast.success(`Allocation updated for template date: ${saveToDate}!`);
          }
        } else if (singleDateMode && displayDate !== getTodayDate()) {
          toast.success(`Allocation updated for ${displayDate}!`);
        } else {
          toast.success(`Allocation updated for ${saveToDate}!`);
        }

        setOriginalAllocations(JSON.parse(JSON.stringify(allocations)));

        const formattedLoadDate = formatDateForAPISimple(displayDate);
        fetchCartAllocation({ restaurantId, date: formattedLoadDate });
      } else {
        toast.error(response.message || "Failed to update allocation");
      }
    } catch (error: any) {
      console.error("Update error:", error);
      const errorMessage =
        error?.data?.message || error?.message || "Error occurred while updating allocation";
      toast.error(`Update failed: ${errorMessage}`);
      if (error?.data) console.error("Full error details:", error.data);
    }
  };

  const cartReservedQuantitiesMap = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    if (productSummaries && Array.isArray(productSummaries)) {
      productSummaries.forEach((productSummary) => {
        const itemId = productSummary.itemId;
        Object.entries(productSummary.carts).forEach(([cartId, cartData]) => {
          if ((cartData as any).channels) {
            Object.entries((cartData as any).channels).forEach(([, channelData]) => {
              if ((channelData as any).reserved && (channelData as any).reserved > 0) {
                if (!map.has(itemId)) map.set(itemId, new Map());
                const cartMap = map.get(itemId)!;
                cartMap.set(
                  cartId,
                  (cartMap.get(cartId) || 0) + ((channelData as any).reserved || 0),
                );
              }
            });
          }
        });
      });
    }
    return map;
  }, [productSummaries]);

  const shouldDisableInputs = isPastDate && singleDateMode;

  const getCartDisplayName = (cart: CartSelection | Cart): string => {
    if ("abbreviation" in cart && cart.abbreviation && cart.abbreviation.trim()) {
      return cart.abbreviation;
    }
    return cart.displayName;
  };

  const isSingleCycle = cycles.length === 1;

  const gridTemplate = useMemo(() => {
    if (isSingleCycle) {
      return `240px 90px minmax(${carts.length * 120}px, 1fr)`;
    }
    const cycleColumns = cycles.map(() => "110px").join(" ");
    const cartsMinWidth = Math.max(carts.length * (80 + cycles.length * 60), 900);
    return `240px ${cycleColumns} minmax(${cartsMinWidth}px, 1fr)`;
  }, [cycles, carts.length, isSingleCycle]);

  const cartCancelledQuantitiesMap = useMemo(() => {
    const map = new Map<string, Map<string, number>>();
    if (productSummaries && Array.isArray(productSummaries)) {
      productSummaries.forEach((productSummary) => {
        const itemId = productSummary.itemId;
        Object.entries(productSummary.carts).forEach(([cartId, cartData]) => {
          if ((cartData as any).channels) {
            Object.entries((cartData as any).channels).forEach(([, channelData]) => {
              if ((channelData as any).cancelled && (channelData as any).cancelled > 0) {
                if (!map.has(itemId)) map.set(itemId, new Map());
                const cartMap = map.get(itemId)!;
                cartMap.set(
                  cartId,
                  (cartMap.get(cartId) || 0) + ((channelData as any).cancelled || 0),
                );
              }
            });
          }
        });
      });
    }
    return map;
  }, [productSummaries]);

  const getNetReservedForProductAndCart = useCallback(
    (itemId: string, cartId: string): number => {
      const productMap = cartReservedQuantitiesMap.get(itemId);
      const reserved = productMap?.get(cartId) || 0;
      const cancelledMap = cartCancelledQuantitiesMap.get(itemId);
      const cancelled = cancelledMap?.get(cartId) || 0;
      return Math.max(0, reserved - cancelled);
    },
    [cartReservedQuantitiesMap, cartCancelledQuantitiesMap],
  );

  const renderGridContent = () => {
    if (isLoading) {
      return (
        <div className="grid-loading-container">
          <LoadingMessage />
        </div>
      );
    }

    if (error) {
      const isNotFoundError =
        ("status" in error && error.status === 404) ||
        ("originalStatus" in error && (error as any).originalStatus === 404) ||
        ("data" in error &&
          error.data &&
          typeof error.data === "object" &&
          "message" in error.data &&
          typeof (error.data as any).message === "string" &&
          (error.data as any).message.includes("not found")) ||
        ("message" in error &&
          typeof (error as any).message === "string" &&
          (error as any).message.includes("404")) ||
        (typeof error === "string" && (error as string).includes("404"));

      if (isNotFoundError) {
        return (
          <div className="grid-error-container">
            <div className="custom-error-message">
              <div className="error-icon">
                <i className="fas fa-calendar-times fa-2x"></i>
              </div>
              <div className="error-title">No Production Plans Found For Today</div>
              <div className="error-subtitle">
                Please check back later or select a different date to view allocations.
              </div>
              {templateMode && !isShowingLoadFromDate && (
                <div className="error-action">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                      const formattedDate = formatDateForAPISimple(displayDate);
                      fetchCartAllocation({ restaurantId, date: formattedDate });
                    }}
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      }

      return (
        <div className="grid-error-container">
          <ErrorMessageComponent />
        </div>
      );
    }

    return (
      <div className={`grid-scroll-container ${isSingleCycle ? "single-cycle-view" : ""}`}>
        <div
          className={`main-allocation-grid ${isSingleCycle ? "single-cycle" : ""}`}
          style={{ gridTemplateColumns: gridTemplate }}
        >
          <div className="column-header product-column-header">
            <span>Product Details</span>
          </div>

          {cycles.map((cycle) => (
            <div
              key={cycle.id}
              className={`column-header cycle-header-cell ${isSingleCycle ? "single-cycle" : ""}`}
            >
              <div className="cycle-header-name" style={{ color: cycleColorMap[cycle.color] }}>
                {cycle.displayName}
                {(templateMode || singleDateMode) && (
                  <Badge
                    bg={templateMode ? "info" : "warning"}
                    className="ms-2"
                    style={{ fontSize: "10px" }}
                  >
                    {templateMode
                      ? isShowingLoadFromDate
                        ? "Source"
                        : "Template"
                      : "Viewing"}
                  </Badge>
                )}
              </div>
              <div className="cycle-header-time">{cycle.time}</div>
            </div>
          ))}

          <div className="cart-headers-section">
            <div
              className={`cart-headers-container ${isSingleCycle ? "single-cycle" : ""}`}
              style={{ "--cycles-count": cycles.length } as React.CSSProperties}
            >
              {carts.map((cart) => {
                const displayName = getCartDisplayName(cart);
                return (
                  <div
                    key={cart.id}
                    className={`cart-header-item ${isSingleCycle ? "single-cycle" : ""}`}
                    style={{ "--cycles-count": cycles.length } as React.CSSProperties}
                  >
                    <div className="cart-header-info">
                      <div className="cart-header-title-row">
                        <span className="cart-name">{displayName}</span>
                        {cart.abbreviation && cart.abbreviation !== displayName && (
                          <Badge className="me-1" bg="secondary">
                            {cart.abbreviation}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="cycle-subheaders-container">
                      <div className={`cycle-subheaders ${isSingleCycle ? "single-cycle" : ""}`}>
                        {cycles.map((cycle) => (
                          <div
                            key={cycle.id}
                            className="cycle-subheader"
                            style={{
                              color: cycleColorMap[cycle.color],
                              backgroundColor: `${cycleColorMap[cycle.color]}15`,
                            }}
                          >
                            {cycle.displayName}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {products.map((product, index) => {
            const tier = tierConfig[product.classFilterTag] || tierConfig.gold;

            let displayName = product.displayName;
            const portionSizePattern = new RegExp(`\\s*${product.portionSize}\\s*$`, "i");
            displayName = displayName.replace(portionSizePattern, "").trim();

            const reservedQuantity = reservedQuantitiesMap.get(product.itemId) || 0;
            const cancelledQuantity = cancelledQuantitiesMap.get(product.itemId) || 0;
            const availableQuantity = calculateAvailableQuantity(product);
            const earliestCycle = getEarliestCycleForProduct(product);
            const allocatedFromEarliestCycle = calculateAllocatedFromEarliestCycle(product);
            const productionFromEarliestCycle = earliestCycle
              ? product.cycleProduction[earliestCycle.id] || 0
              : 0;

            return (
              <React.Fragment key={product.uniqueKey}>
                <div
                  className={`data-cell product-info-cell ${index % 2 === 0 ? "row-even" : "row-odd"}`}
                >
                  <div className="product-info-container">
                    <span className="product-tier-icon" style={{ color: tier.color }}>
                      {tier.icon}
                    </span>
                    <div className="product-details">
                      <div className="product-name">{displayName}</div>
                      <div className="product-subdetails">
                        <div
                          className="counts-container"
                          style={{
                            display: "flex",
                            gap: "8px",
                            flexWrap: "wrap",
                            marginTop: "4px",
                          }}
                        >
                          <div className="count-item">
                            <Badge
                              bg="secondary"
                              className="count-badge"
                              title={`Orders: ${reservedQuantity} | Cancelled: ${cancelledQuantity}`}
                              style={{
                                fontSize: "10px",
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                              }}
                            >
                              <span className="count-label" style={{ fontWeight: "bold" }}>
                                Ord:
                              </span>
                              <span className="count-value">{reservedQuantity}</span>
                              {cancelledQuantity > 0 && (
                                <span
                                  className="cancelled-indicator"
                                  style={{ marginLeft: "2px", fontSize: "8px", opacity: 0.8 }}
                                >
                                  (Can: {cancelledQuantity})
                                </span>
                              )}
                            </Badge>
                          </div>

                          <div className="count-item">
                            <Badge
                              bg={availableQuantity >= 0 ? "success" : "danger"}
                              className="count-badge"
                              title={
                                earliestCycle
                                  ? `Calculation: ${productionFromEarliestCycle} (${earliestCycle.displayName} production) - ` +
                                    `${allocatedFromEarliestCycle} (allocated to carts) - ` +
                                    `${reservedQuantity} (orders) + ${cancelledQuantity} (cancelled) = ${availableQuantity}`
                                  : "No production in any cycle"
                              }
                              style={{
                                fontSize: "10px",
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                              }}
                            >
                              <span className="count-label" style={{ fontWeight: "bold" }}>
                                Avl:
                              </span>
                              <span className="count-value">{availableQuantity}</span>
                            </Badge>
                          </div>

                          {overviewLoading && (
                            <div className="count-item">
                              <Badge bg="light" style={{ fontSize: "8px", color: "#666" }}>
                                Loading...
                              </Badge>
                            </div>
                          )}
                        </div>

                        {(templateMode || singleDateMode) &&
                          product.cycleProduction &&
                          Object.values(product.cycleProduction).some((qty) => qty > 0) && (
                            <Badge
                              bg={templateMode ? "info" : "warning"}
                              className="ms-2"
                              style={{ fontSize: "10px", marginTop: "4px" }}
                            >
                              {templateMode
                                ? isShowingLoadFromDate
                                  ? "Source"
                                  : "Template"
                                : "Viewing"}
                            </Badge>
                          )}
                      </div>
                    </div>
                  </div>
                </div>

                {cycles.map((cycle) => (
                  <div
                    key={cycle.id}
                    className={`data-cell cycle-production-cell ${isSingleCycle ? "single-cycle" : ""}`}
                  >
                    <span
                      className="cycle-production-value"
                      style={{
                        color: cycleColorMap[cycle.color],
                        backgroundColor: `${cycleColorMap[cycle.color]}15`,
                      }}
                    >
                      {product.cycleProduction[cycle.id] || 0}
                    </span>
                  </div>
                ))}

                <div
                  className={`data-cell cart-allocation-cell ${index % 2 === 0 ? "row-even" : "row-odd"}`}
                >
                  <div className={`cart-inputs-grid ${isSingleCycle ? "single-cycle" : ""}`}>
                    {carts.map((cart) => {
                      const alloc = allocations[product.uniqueKey]?.[cart.id] || {};
                      const cartReservedCount = getNetReservedForProductAndCart(
                        product.itemId,
                        cart.id,
                      );

                      return (
                        <div
                          key={cart.id}
                          className={`cart-input-group ${isSingleCycle ? "single-cycle" : ""}`}
                          style={{ "--cycles-count": cycles.length } as React.CSSProperties}
                        >
                          <div className="cart-input-header">
                            {cartReservedCount > 0 && (
                              <Badge
                                bg="secondary"
                                className="cart-reserved-badge"
                                title={`Reserved for this cart: ${cartReservedCount}`}
                                style={{
                                  fontSize: "9px",
                                  padding: "2px 4px",
                                  display: "inline-block",
                                }}
                              >
                                Pre-Orders: {cartReservedCount}
                              </Badge>
                            )}
                          </div>

                          <div className="cycle-inputs-container">
                            <div
                              className={`cycle-inputs-row ${isSingleCycle ? "single-cycle" : ""}`}
                            >
                              {cycles.map((cycle) => {
                                const maxAllocation = product.cycleProduction[cycle.id] || 0;
                                const hasChanged =
                                  isUpdateMode &&
                                  hasAllocationChanged(product.uniqueKey, cart.id, cycle.id);
                                const availableAllocation = getAvailableAllocation(
                                  product.uniqueKey,
                                  cart.id,
                                  cycle.id,
                                );
                                const currentValue = alloc[cycle.id] || 0;
                                const otherCartsAllocation = carts.reduce((sum, c) => {
                                  if (c.id === cart.id) return sum;
                                  return (
                                    sum +
                                    (allocations[product.uniqueKey]?.[c.id]?.[cycle.id] || 0)
                                  );
                                }, 0);
                                const isThisInputOverflowing =
                                  currentValue + otherCartsAllocation > maxAllocation;

                                return (
                                  <div key={cycle.id} className="cycle-input-wrapper">
                                    <div className="cycle-input-with-reserved">
                                      <input
                                        type="number"
                                        min="0"
                                        max={maxAllocation}
                                        value={alloc[cycle.id] || ""}
                                        onChange={(e) => {
                                          if (shouldDisableInputs) return;
                                          const rawValue = e.target.value;
                                          if (rawValue === "") {
                                            updateAllocation(
                                              product.uniqueKey,
                                              cart.id,
                                              cycle.id,
                                              0,
                                            );
                                            return;
                                          }
                                          const numberMatch = rawValue.match(/-?\d+(\.\d+)?/);
                                          const value = numberMatch
                                            ? Number.parseInt(numberMatch[0])
                                            : 0;
                                          updateAllocation(
                                            product.uniqueKey,
                                            cart.id,
                                            cycle.id,
                                            value,
                                          );
                                        }}
                                        onBlur={(e) => {
                                          if (shouldDisableInputs) return;
                                          const value = Number.parseInt(e.target.value) || 0;
                                          if (value > availableAllocation) {
                                            updateAllocation(
                                              product.uniqueKey,
                                              cart.id,
                                              cycle.id,
                                              availableAllocation,
                                            );
                                          }
                                        }}
                                        onWheel={(e) => e.currentTarget.blur()}
                                        onPaste={(e) => {
                                          e.preventDefault();
                                          const pastedText = e.clipboardData.getData("text");
                                          const numberMatch = pastedText.match(/-?\d+(\.\d+)?/);
                                          const value = numberMatch
                                            ? Number.parseInt(numberMatch[0])
                                            : 0;
                                          updateAllocation(
                                            product.uniqueKey,
                                            cart.id,
                                            cycle.id,
                                            value,
                                          );
                                        }}
                                        className={`cycle-input ${hasChanged ? "changed-input" : ""} ${isThisInputOverflowing ? "error-input" : ""} ${templateMode ? "template-input" : singleDateMode ? "single-date-input" : ""} ${shouldDisableInputs ? "cycle-input--disabled" : ""}`}
                                        style={{
                                          borderColor: isThisInputOverflowing
                                            ? "#dc3545"
                                            : cycleColorMap[cycle.color],
                                          backgroundColor: hasChanged
                                            ? `${cycleColorMap[cycle.color]}15`
                                            : isThisInputOverflowing
                                              ? "#fff5f5"
                                              : templateMode
                                                ? "#e8f4f8"
                                                : singleDateMode
                                                  ? "#fff8e1"
                                                  : "white",
                                        }}
                                        placeholder="0"
                                        disabled={shouldDisableInputs}
                                        title={
                                          shouldDisableInputs
                                            ? "Cannot modify past dates"
                                            : isThisInputOverflowing
                                              ? `Exceeds available allocation. Other carts: ${otherCartsAllocation}, Available: ${Math.max(0, maxAllocation - otherCartsAllocation)}`
                                              : `Max: ${maxAllocation}, Available: ${availableAllocation}`
                                        }
                                      />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          <>
            <div className="data-cell totals-label-cell">
              <span className="totals-label">TOTALS</span>
            </div>

            {cycles.map((cycle) => (
              <div key={cycle.id} className="data-cell cycle-total-cell">
                <span
                  className="cycle-total-value"
                  style={{
                    color: cycleColorMap[cycle.color],
                    borderColor: cycleColorMap[cycle.color],
                  }}
                >
                  {getCycleTotals[cycle.id]}
                </span>
              </div>
            ))}

            <div className="data-cell carts-total-cell">
              <div className={`carts-totals-grid ${isSingleCycle ? "single-cycle" : ""}`}>
                {carts.map((cart) => {
                  const total = getCartCycleTotal(cart.id);
                  return (
                    <div
                      key={cart.id}
                      className={`cart-total-item ${isSingleCycle ? "single-cycle" : ""}`}
                      style={{ "--cycles-count": cycles.length } as React.CSSProperties}
                    >
                      {total}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`cart-allocation-containers ${templateMode ? "template-mode" : singleDateMode ? "single-date-mode" : ""}`}
      style={{ "--cycles-count": cycles.length } as React.CSSProperties}
    >
      <div className="cart-allocation-header">
        <div className="header-title-section">
          <h1 className="main-title">
            Allocate Cycle Output to Carts
            {templateMode ? (
              <Badge bg="info" className="ms-2">
                <Copy size={12} className="me-1" />
                Template Mode{isShowingLoadFromDate ? " (Source)" : ""}
              </Badge>
            ) : singleDateMode ? (
              <Badge bg="warning" className="ms-2">
                <Eye size={12} className="me-1" />
                Viewing Date
              </Badge>
            ) : null}
          </h1>
          <div className="date-display-info">
            {templateMode ? (
              <div className="template-info">
                <span className="text-info">
                  <i className="fas fa-copy me-1"></i>
                  Template Mode:{" "}
                  {isShowingLoadFromDate ? "Showing source data from" : "Showing target data for"}{" "}
                  <strong>{displayDate}</strong>
                  {saveToDate !== displayDate && (
                    <span className="ms-2">
                      • Save will create allocations for: <strong>{saveToDate}</strong>
                    </span>
                  )}
                </span>
              </div>
            ) : singleDateMode ? (
              <div className="single-date-info">
                <span className="text-warning">
                  <i className="fas fa-calendar me-1"></i>
                  Viewing date: <strong>{displayDate}</strong>
                  {saveToDate !== displayDate && (
                    <span className="ms-2">
                      • Save will create allocations for: <strong>{saveToDate}</strong>
                    </span>
                  )}
                </span>
              </div>
            ) : (
              <p className="subtitle">
                Showing data for: <strong>{displayDate}</strong>
              </p>
            )}
          </div>
        </div>

        <div className="header-actions-section">
          <div className="cart-name-search-container">
            <div className="cart-search-wrapper mt-4">
              <Form.Control
                type="text"
                placeholder="Search cart name..."
                value={cartNameSearch}
                onChange={(e) => handleCartNameSearch(e.target.value)}
                className="cart-name-search-input"
                style={{ width: "200px" }}
              />
            </div>
          </div>

          {cycleSelections.length > 0 && (
            <div className="cycle-selection-container">
              <Dropdown
                show={showCycleDropdown}
                onToggle={(show) => setShowCycleDropdown(show)}
                autoClose="outside"
              >
                <Dropdown.Toggle
                  as={CustomToggle}
                  variant="outline-primary"
                  className="cycle-filter-btn"
                >
                  <Filter size={14} className="me-1" />
                  Filter Cycles ({selectedCycles.length}/{cycleSelections.length})
                  <ChevronDown size={14} className="ms-1" />
                </Dropdown.Toggle>

                <Dropdown.Menu as={CustomDropDown} className="cycle-dropdown-menu">
                  <div className="cycle-dropdown-header">
                    <div className="cycle-dropdown-actions">
                      <button className="btn-link btn-sm" onClick={(e) => selectAllCycles(e)}>
                        Select All
                      </button>
                      <button
                        className="btn-link btn-sm ms-2"
                        onClick={(e) => deselectAllCycles(e)}
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="cycle-list-container">
                    {cycleSelections.map((cycle) => {
                      const cycleInfo = allCycles.find((c) => c.id === cycle.id);
                      return (
                        <div
                          key={cycle.id}
                          className="dropdown-item cycle-item"
                          onClick={(e) => toggleCycleSelection(cycle.id, e)}
                        >
                          <div className="cycle-item-content">
                            <div className="cycle-checkbox">
                              {cycle.selected && <Check size={14} />}
                            </div>
                            <div className="cycle-info">
                              <div className="cycle-name">
                                {cycle.displayName}
                                {cycleInfo && (
                                  <span
                                    className="cycle-color-indicator"
                                    style={{
                                      backgroundColor: cycleColorMap[cycleInfo.color],
                                    }}
                                  />
                                )}
                              </div>
                              {cycleInfo && (
                                <div className="cycle-time">{cycleInfo.time}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}

          {cartSelections.length > 0 && (
            <div className="cycle-selection-container">
              <Dropdown
                show={showCartDropdown}
                onToggle={(show) => setShowCartDropdown(show)}
                autoClose="outside"
              >
                <Dropdown.Toggle
                  as={CustomToggle}
                  variant="outline-primary"
                  className="cycle-filter-btn"
                >
                  <Filter size={14} className="me-1" />
                  Filter Carts ({selectedCarts.length}/{cartSelections.length})
                  <ChevronDown size={14} className="ms-1" />
                </Dropdown.Toggle>

                <Dropdown.Menu
                  as={CustomDropDown}
                  className="cycle-dropdown-menu cart-dropdown-menu"
                >
                  <div className="cycle-dropdown-header">
                    <div className="cycle-dropdown-actions">
                      <button className="btn-link btn-sm" onClick={(e) => selectAllCarts(e)}>
                        Select All
                      </button>
                      <button
                        className="btn-link btn-sm ms-2"
                        onClick={(e) => deselectAllCarts(e)}
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="cycle-list-container">
                    {filteredCartSelections.map((cart) => (
                      <div
                        key={cart.id}
                        className="dropdown-item cycle-item cart-item"
                        onClick={(e) => toggleCartSelection(cart.id, e)}
                      >
                        <div className="cycle-item-content">
                          <div className="cycle-checkbox">
                            {cart.selected && <Check size={14} />}
                          </div>
                          <div className="cycle-info">
                            <div className="cycle-name">
                              {getCartDisplayName(cart)}
                              {cart.abbreviation && cart.abbreviation !== cart.displayName && (
                                <span className="cart-full-name">({cart.displayName})</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredCartSelections.length === 0 && (
                      <div className="no-results">
                        No carts found matching "{cartSearchQuery}"
                      </div>
                    )}
                  </div>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}

          {!isUpdateMode && (
            <button
              className={`update-button ${templateMode ? "template-save" : singleDateMode ? "single-date-save" : ""} ${shouldDisableInputs ? "update-button--disabled" : ""}`}
              onClick={handleSaveAllocation}
              disabled={
                isSaving ||
                isLoading ||
                selectedCycles.length === 0 ||
                selectedCarts.length === 0 ||
                shouldDisableInputs
              }
              title={
                shouldDisableInputs
                  ? "Cannot modify past dates"
                  : templateMode
                    ? `Save new allocation for template date: ${saveToDate}`
                    : singleDateMode
                      ? `Save new allocation for ${displayDate}`
                      : "Save new allocation"
              }
            >
              {shouldDisableInputs ? (
                "View Only"
              ) : isSaving ? (
                <>
                  <span className="spinner"></span>Saving...
                </>
              ) : (
                <>Save Allocation</>
              )}
            </button>
          )}

          {isUpdateMode && (
            <button
              className={`update-button ${templateMode ? "template-update" : singleDateMode ? "single-date-update" : ""} ${shouldDisableInputs ? "update-button--disabled" : ""}`}
              onClick={handleUpdateAllocation}
              disabled={
                isSaving ||
                isLoading ||
                !hasAnyAllocationChanged() ||
                selectedCycles.length === 0 ||
                selectedCarts.length === 0 ||
                shouldDisableInputs
              }
              title={
                shouldDisableInputs
                  ? "Cannot modify past dates"
                  : templateMode
                    ? `Update existing allocation for template date: ${saveToDate}`
                    : singleDateMode
                      ? `Update existing allocation for ${displayDate}`
                      : "Update existing allocation"
              }
            >
              {shouldDisableInputs ? (
                "View Only"
              ) : isSaving ? (
                <>
                  <span className="spinner"></span>Updating...
                </>
              ) : (
                <>Update Allocation</>
              )}
            </button>
          )}
        </div>
      </div>

      {products.some((product) =>
        cycles.some((cycle) => getAllocationError(product.uniqueKey, cycle.id) !== null),
      ) && (
        <div className="validation-summary">
          <div className="validation-summary-header">⚠️ Validation Issues</div>
          <div className="validation-summary-content">
            Some allocations exceed production limits. Please adjust allocations to match
            production quantities.
          </div>
        </div>
      )}
      <div className="grid-scroll-container">{renderGridContent()}</div>
    </div>
  );
}